import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { NooraniAttendanceApi, NooraniStudentAttendanceRow } from '@/services/noorani-attendance.api';
import { NooraniGroupsApi } from '@/services/noorani-groups.api';
import { SchoolsApi } from '@/services/superadmin.api';
import { OrgPortalApi } from '@/services/org.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';

const columnHelper = createColumnHelper<NooraniStudentAttendanceRow>();

/**
 * Noorani Attendance Report — a pure filter over the SAME `attendance` data
 * every other attendance screen reads, scoped to a group's members. No new
 * table; see NooraniAttendanceService.studentsReport()'s doc comment.
 */
export function NooraniAttendanceReportPage({ scope }: { scope: 'superadmin' | 'organization' | 'school' }) {
  const showSchoolPicker = scope !== 'school';
  const dt = useDataTable({ defaultLimit: 20 });

  const [schoolId, setSchoolId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    setGroupId('');
  }, [schoolId]);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-noorani-attendance-filter', scope],
    queryFn: async () =>
      scope === 'organization' ? (await OrgPortalApi.listSchools({ limit: 100 })).items : (await SchoolsApi.list({ limit: 100 })).items,
    enabled: showSchoolPicker,
  });

  const { data: groupsPage } = useQuery({
    queryKey: ['noorani-groups-for-attendance-filter', schoolId, scope],
    queryFn: () => NooraniGroupsApi.list({ schoolId: showSchoolPicker ? schoolId || undefined : undefined, limit: 100 }),
    enabled: !showSchoolPicker || !!schoolId,
  });
  const groups = groupsPage?.items ?? [];

  const { data, isFetching } = useQuery({
    queryKey: ['noorani-attendance-students', { ...dt.params, groupId, dateFrom, dateTo }],
    queryFn: () => NooraniAttendanceApi.students({ ...dt.params, groupId, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined }),
    enabled: !!groupId,
    placeholderData: keepPreviousData,
  });

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    columnHelper.accessor('studentName', { header: 'Student Name' }),
    columnHelper.accessor('admissionNo', { header: 'Admission #' }),
    columnHelper.display({
      id: 'class',
      header: 'Class',
      cell: (ctx) => `${ctx.row.original.className} - ${ctx.row.original.sectionName}`,
    }),
    columnHelper.accessor('total', { header: 'Total Classes' }),
    columnHelper.accessor('present', { header: 'Present' }),
    columnHelper.accessor('absent', { header: 'Absent' }),
    columnHelper.accessor('late', { header: 'Late' }),
    columnHelper.accessor('percentage', {
      header: 'Percentage',
      cell: (ctx) => <Badge tone={ctx.getValue() >= 75 ? 'green' : 'amber'}>{ctx.getValue()}%</Badge>,
    }),
  ];

  return (
    <>
      <PageHeader
        title="Noorani Attendance Report"
        description="Student attendance for a Noorani group's members, filtered by date range."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {showSchoolPicker && (
          <Select className="!w-52" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
            <option value="">— Select school —</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        )}
        <Select className="!w-56" value={groupId} onChange={(e) => setGroupId(e.target.value)} disabled={showSchoolPicker && !schoolId}>
          <option value="">— Select Noorani group —</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </Select>
        <Input type="date" className="!w-40" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} placeholder="From" />
        <Input type="date" className="!w-40" value={dateTo} onChange={(e) => setDateTo(e.target.value)} placeholder="To" />
      </div>

      {!groupId ? (
        <div className="card p-8 text-center text-slate-500">Select a Noorani group to view its attendance.</div>
      ) : (
        <DataTable
          columns={columns}
          result={data}
          state={dt}
          loading={isFetching}
          emptyMessage="No students assigned to this group yet."
          getRowId={(row) => row.studentId}
        />
      )}
    </>
  );
}
